import { useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

/**
 * Custom hook to get a PostHog feature flag value.
 * Waits for PostHog feature flags to load before returning the value.
 * Follows the same pattern as charger.component.tsx which uses onFeatureFlags.
 *
 * @param flagName - The name of the feature flag to check
 * @param defaultValue - Optional default value to use if PostHog is not available or flags haven't loaded yet. Defaults to false.
 * @returns The feature flag value (boolean)
 */
export const usePostHogFeatureFlag = (flagName: string, defaultValue: boolean = false): boolean => {
  const posthog = usePostHog();
  const [isEnabled, setIsEnabled] = useState<boolean>(defaultValue);

  useEffect(() => {
    if (posthog) {
      // Check immediately in case flags are already loaded
      const currentValue = posthog.isFeatureEnabled(flagName);
      if (currentValue !== undefined) {
        setIsEnabled(currentValue);
      }

      // Also listen for when flags finish loading (matches charger.component.tsx pattern)
      posthog.onFeatureFlags(function () {
        // feature flags should be available at this point
        setIsEnabled(posthog.isFeatureEnabled(flagName) ?? defaultValue);
      });
    }
  }, [posthog, flagName, defaultValue]);

  return isEnabled;
};
